import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "./index.css"; // Ensure Tailwind is applied
import './Calendar.css';
import axios from "axios"; // Ensure Axios is available for API calls
import Select from "react-select";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const post = (endpoint, data) =>
  axios.post(`${API_BASE_URL}${endpoint}`, data);

const VintageFilter = () => (
  <>
    {/* The Radial Gradient "Lens" Filter */}
    <div 
      className="absolute inset-0 z-10 pointer-events-none"
      style={{
        background: "radial-gradient(circle, transparent 20%, rgba(44, 34, 20, 0.4) 70%, rgba(20, 15, 10, 0.8) 100%)",
        mixBlendMode: "multiply"
      }}
    />
    {/* The Warm Color Wash */}
    <div className="absolute inset-0 z-10 bg-orange-900/5 pointer-events-none mix-blend-screen" />
  </>
);

const Home = () => (
  <div className="relative w-screen h-screen">
    
    {/* Background Image */}
    <div
      className="absolute inset-0"
      style={{
        backgroundImage: "url('/images/tpn.jpeg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "scroll",
      }}
    />

    {/* Vintage Overlay */}
    <VintageFilter />

    {/* Text Content */}
    <div className="relative z-20 flex flex-col items-center justify-center h-screen text-white text-center px-6 space-y-8">
      <h1
        className="text-6xl font-bold leading-tight drop-shadow-lg tracking-wide"
        style={{
          fontFamily: "League Spartan, sans-serif",
          textShadow: "2px 2px 10px rgba(0, 0, 0, 0.8)",
        }}
      >
        Stop refreshing. Start golfing.
        <br />
        Book smarter. Play more.
      </h1>
    </div>

  </div>
);

const sections = [
  {
    title: "How Does It Work?",
    lines: [
      "SoCal TeeTimes continuously searches for available tee times based on your selected date, time, and course.",
      "When a match is found, it books it instantly using your saved information.",
      "No more refreshing pages all day — socalteetimes does the work for you.",
    ],
  },
  {
    title: "Step 1: Add Your Credentials",
    lines: [
      "Add your login credentials for each course you'd like to book.",
      "This allows the system to authenticate on your behalf.",
      "Don’t worry — all passwords are securely encrypted.",
    ],
  },
  {
    title: "Step 2: Choose Your Preferences",
    lines: [
      "Pick your preferred course, select the date(s), and define your ideal time range.",
      "You can also choose the number of players.",
    ],
  },
  {
    title: "Step 3: Let It Work",
    lines: [
      "Once saved, the app starts automatically watching for available tee times.",
      "When a match is found, your reservation is booked — and you'll be notified!",
    ],
  },
];

const HowItWorks = () => {
  return (
    <div className="relative w-screen">

      {/* Background Image */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: "url('/images/tpn.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "scroll",
        }}
      />

      {/* Vintage Overlay */}
      <VintageFilter />

      {/* Content */}
      <div className="relative z-20 snap-y snap-mandatory overflow-y-auto min-h-screen" style={{scrollSnapType: 'y mandatory', height: '100vh'}}>
        {sections.map((section, index) => (
          <div
            key={index}
            className="snap-start flex flex-col justify-center items-center text-center px-6 h-screen"
            style={{scrollSnapAlign: 'start', height: '100vh'}}>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6 drop-shadow-[0_4px_12px_rgba(0,0,0,0.9)]">
              {section.title}
            </h1>

            <div className="text-lg sm:text-xl max-w-3xl space-y-4 drop-shadow-[0_4px_12px_rgba(0,0,0,0.9)]">
              {section.lines.map((line, i) => (
                <p key={i}>{line}</p>
              ))}
            </div>

          </div>
        ))}
      </div>

    </div>
  );
};

const AuthPage = ({ setIsLoggedIn }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const navigate = useNavigate();

  const handleAuth = async () => {
    const endpoint = isLogin ? "/login" : "/register";
    try {
      const response = await axios.post(`${API_BASE_URL}${endpoint}`, { email, password });
      setMessage(response.data.message);

      if (isLogin) {
        localStorage.setItem("userEmail", email);
        localStorage.setItem("isLoggedIn", "true");
        setIsLoggedIn(true);
        setMessage("Log in successful! Redirecting to Auto-Book...");
        setTimeout(() => {
          navigate("/auto-book");
        }, 1200);
      } else {
        setMessage(" Check your email to confirm your account.");
      }
    } catch (error) {
      setMessage(error.response?.data?.error || "Operation failed");
    }
  };
  
  return (
  <div
    className="min-h-screen flex flex-col items-center justify-center px-4"
    style={{
      fontFamily: 'League Spartan, sans-serif',
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(4px)',
      WebkitBackdropFilter: 'blur(4px)',
    }}
  >
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleAuth();
      }}
      className="bg-white/10 backdrop-blur-md p-8 rounded-xl shadow-lg w-full max-w-md text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)]"
    >
      <h1 className="text-3xl font-bold text-center mb-6">
        {isLogin ? "Login" : "Register"}
      </h1>

      <input
        type="email"
        placeholder="Email"
        className="p-3 border border-white/20 rounded w-full mb-4 bg-black/30 text-white placeholder-white/80"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <input
        type="password"
        placeholder="Password"
        className="p-3 border border-white/20 rounded w-full mb-4 bg-black/30 text-white placeholder-white/80"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />

      <button
        type="submit"
        className="w-full py-3 bg-green-600 rounded-lg hover:bg-green-700 transition"
      >
        {isLogin ? "Login" : "Register"}
      </button>

      <button
        type="button"
        onClick={() => setIsLogin(!isLogin)}
        className="mt-4 text-sm text-blue-300 hover:underline w-full text-center"
      >
        {isLogin ? "Need an account? Register" : "Already have an account? Login"}
      </button>

      <button
        type="button"
        onClick={() => window.location.assign("/forgot-password")}
        className="mt-2 text-sm text-blue-300 hover:underline w-full text-center"
      >
        Forgot your password?
      </button>
            

      {message && (
        <p className="mt-4 text-sm text-yellow-300 text-center">{message}</p>
      )}
    </form>
  </div>
  );
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const userEmail = localStorage.getItem("userEmail") || "";
  const [reservations, setReservations] = useState([]);
  const [, setCredentials] = useState([]);
  const [editCredential, setEditCredential] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userEmail) {
      navigate("/auth");
    }
  }, [userEmail, navigate]);

  const fetchReservations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await post("/get-reservations", { email: userEmail });
      setReservations(response.data.reservations || []);
    } catch (error) {
      console.error("Error fetching reservations:", error);
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  const fetchCredentials = useCallback(async () => {
    try {
      const response = await post("/get-credentials", { email: userEmail });
      setCredentials(response.data.credentials || []);
    } catch (error) {
      console.error("Error fetching credentials:", error);
    }
  }, [userEmail]);

  useEffect(() => {
    fetchReservations();
    fetchCredentials();
  }, [fetchReservations, fetchCredentials]);

  const cancelReservation = async (reservationId) => {
    try {
      await post("/cancel", { reservationId });
      setReservations((prev) => prev.filter((res) => res.reservationId !== reservationId));
      alert(" Reservation canceled successfully!");
    } catch (error) {
      console.error("Error canceling reservation:", error);
      alert("Error canceling reservation.");
    }
  };

  // Cancel all active reservations test
  const cancelAllReservations = async () => {
    if (!window.confirm("Are you sure you want to cancel ALL active reservations and notifications?")) return;
    try {
      for (const res of activeReservations) {
        await post("/cancel", { reservationId: res.reservationId });
      }
      setReservations((prev) => prev.filter((res) => (res.status || "").toLowerCase() === "booked" || (res.status || "").toLowerCase() === "canceled"));
      alert("All active reservations and notifications canceled.");
    } catch (error) {
      console.error("Error canceling all reservations:", error);
      alert("Error canceling all reservations.");
    }
  };

  const handleUpdateCredential = async () => {
    if (!editCredential?.new_username || !editCredential?.new_password) {
      alert("Please fill in both fields.");
      return;
    }

    try {
      await post("/update-credentials", {
        email: userEmail,
        course: editCredential.course,
        new_username: editCredential.new_username,
        new_password: editCredential.new_password,
      });

      setCredentials((prev) =>
        prev.map((cred) =>
          cred.course === editCredential.course
            ? { ...cred, username: editCredential.new_username }
            : cred
        )
      );
      alert(" Credentials updated successfully!");
      setEditCredential(null);
    } catch (error) {
      console.error("Error updating credentials:", error);
      alert("Error updating credentials.");
    }
  };

  // NEW: treat anything not finalized as "active"
  const activeReservations = reservations.filter(
    (r) => (r.status || "").toLowerCase() !== "booked" && (r.status || "").toLowerCase() !== "canceled"
  );

  return (
    <div className="relative w-screen h-screen text-white" style={{ fontFamily: 'League Spartan, sans-serif' }}>
      {/* Background Image */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: "url('/images/tpn.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "scroll",
        }}
      />
      {/* Vintage Overlay */}
      <VintageFilter />

      {/* Content */}
      <div className="relative z-20 max-w-4xl mx-auto pt-32 px-6">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-2">
          <h1 className="text-4xl font-bold drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
            Active Reservations
          </h1>
          <div className="flex gap-2">
            <button
              onClick={fetchReservations}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 border border-white/30 rounded-lg text-sm"
              disabled={loading}
            >
              {loading ? "Refreshing…" : "Refresh"}
            </button>
            {activeReservations.length > 0 && (
              <button
                onClick={cancelAllReservations}
                className="px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 border border-red-900 text-sm"
              >
                Cancel All
              </button>
            )}
          </div>
        </div>

        {activeReservations.length > 0 ? (
          <ul className="flex flex-col gap-4">
            {activeReservations.map((res) => (
              <li
                key={res.reservationId}
                className="bg-white/90 text-black rounded-lg shadow-lg p-4 flex flex-wrap gap-3 md:gap-0 md:flex-nowrap justify-between items-center"
              >
                <span className="text-base font-medium">
                  {res.date} | {res.earliestTime} - {res.latestTime} at {res.course}
                </span>

                {/* Status pill */}
                <span className="px-3 py-1 rounded-full text-sm bg-gray-800 text-white">
                  {(res.status || "unknown").toLowerCase()}
                </span>

                <button
                  onClick={() => cancelReservation(res.reservationId)}
                  className="px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition"
                >
                  Cancel
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-white/80">No active reservations.</p>
        )}

        {/* Edit Modal */}
        {editCredential && (
          <div className="fixed inset-0 overflow-y-auto flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white p-6 rounded-lg shadow-lg w-96 text-black">
              <h2 className="text-2xl font-semibold mb-4">
                Edit Credentials for {editCredential.course}
              </h2>
              <input
                type="text"
                placeholder="New Username"
                className="p-2 border rounded w-full mb-4"
                value={editCredential.new_username}
                onChange={(e) =>
                  setEditCredential({
                    ...editCredential,
                    new_username: e.target.value,
                  })
                }
              />
              <input
                type="password"
                placeholder="New Password"
                className="p-2 border rounded w-full mb-4"
                value={editCredential.new_password}
                onChange={(e) =>
                  setEditCredential({
                    ...editCredential,
                    new_password: e.target.value,
                  })
                }
              />
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setEditCredential(null)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateCredential}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setMsg("");
    try {
      const res = await axios.post(`${API_BASE_URL}/forgot-password`, { email });
      setMsg(res.data.message || "If that email exists, a reset link was sent.");
    } catch {
      // Keep same message to avoid leaking existence
      setMsg("If that email exists, a reset link was sent.");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center text-white">
      <form
        onSubmit={submit}
        className="bg-black bg-opacity-60 p-8 rounded-lg text-center max-w-md shadow-lg w-full"
      >
        <h1 className="text-2xl font-bold mb-4">Forgot Password</h1>
        <input
          type="email"
          placeholder="Your account email"
          className="p-3 border border-white/20 rounded w-full mb-4 bg-black/30 text-white placeholder-white/80"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <button type="submit" className="w-full py-3 bg-green-600 rounded-lg hover:bg-green-700 transition">
          Send reset link
        </button>
        {msg && <p className="mt-4 text-sm text-yellow-300">{msg}</p>}
      </form>
    </div>
  );
};

const ResetPassword = () => {
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [msg, setMsg] = useState("");
  const navigate = useNavigate();

  const token = new URLSearchParams(window.location.search).get("token");

  const submit = async (e) => {
    e.preventDefault();
    setMsg("");
    if (!token) {
      setMsg(" Invalid or missing token.");
      return;
    }
    if (newPw.length < 8) {
      setMsg(" Password must be at least 8 characters.");
      return;
    }
    if (newPw !== confirmPw) {
      setMsg(" Passwords do not match.");
      return;
    }
    try {
      const res = await axios.post(`${API_BASE_URL}/reset-password`, {
        token,
        new_password: newPw,
      });
      setMsg(res.data.message || " Password updated!");
      // Optional: redirect to login after a moment
      setTimeout(() => navigate("/auth"), 1500);
    } catch (err) {
      setMsg(err.response?.data?.error || " Reset failed.");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center text-white">
      <form
        onSubmit={submit}
        className="bg-black bg-opacity-60 p-8 rounded-lg text-center max-w-md shadow-lg w-full"
      >
        <h1 className="text-2xl font-bold mb-4">Reset Password</h1>
        <input
          type="password"
          placeholder="New password"
          className="p-3 border border-white/20 rounded w-full mb-4 bg-black/30 text-white placeholder-white/80"
          value={newPw}
          onChange={(e) => setNewPw(e.target.value)}
          required
        />
        <input
          type="password"
          placeholder="Confirm new password"
          className="p-3 border border-white/20 rounded w-full mb-4 bg-black/30 text-white placeholder-white/80"
          value={confirmPw}
          onChange={(e) => setConfirmPw(e.target.value)}
          required
        />
        <button type="submit" className="w-full py-3 bg-green-600 rounded-lg hover:bg-green-700 transition">
          Update password
        </button>
        {msg && <p className="mt-4 text-sm text-yellow-300">{msg}</p>}
      </form>
    </div>
  );
};

const PreviousReservations = () => {
  const userEmail = localStorage.getItem("userEmail") || "";
  const [reservations, setReservations] = useState([]);

  useEffect(() => {
    const fetchPreviousReservations = async () => {
      try {
        const response = await post("/get-reservations", { email: userEmail });
        const booked = (response.data.reservations || []).filter(
          (res) => (res.status || "").toLowerCase() === "booked"
        );
        setReservations(booked);
      } catch (error) {
        console.error("Error fetching previous reservations:", error);
      }
    };
    fetchPreviousReservations();
  }, [userEmail]);

  return (
    <div className="relative w-screen h-screen text-white" style={{ fontFamily: 'League Spartan, sans-serif' }}>
      {/* Background Image */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: "url('/images/tpn.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "scroll",
        }}
      />
      {/* Vintage Overlay */}
      <VintageFilter />

      {/* Content */}
      <div className="relative z-20 max-w-4xl mx-auto pt-32 px-6">
        <h1 className="text-4xl font-bold mb-8 text-center drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
          Previous Reservations
        </h1>

        {reservations.length > 0 ? (
          <ul className="flex flex-col gap-4">
            {reservations.map((res) => (
              <li
                key={res.reservationId}
                className="bg-white/90 text-black rounded-lg shadow-lg p-4"
              >
                <span className="block text-base font-medium">
                  {res.date} | {res.earliestTime} - {res.latestTime} at {res.course}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-white/80 text-center">
            No previous reservations found.
          </p>
        )}
      </div>
    </div>
  );
};

const CredentialsPage = () => {
  const navigate = useNavigate();
  const userEmail = localStorage.getItem("userEmail") || "";
  const [credentials, setCredentials] = useState([]);
  const [editCredential, setEditCredential] = useState(null);

  useEffect(() => {
    if (!userEmail) {
      navigate("/auth");
    }
  }, [userEmail, navigate]);

  const fetchCredentials = useCallback(async () => {
    try {
      const response = await post("/get-credentials", { email: userEmail });
      setCredentials(response.data.credentials || []);
    } catch (error) {
      console.error("Error fetching credentials:", error);
    }
  }, [userEmail]);

  useEffect(() => {
    fetchCredentials();
  }, [fetchCredentials]);

  const handleDeleteCredential = async (course) => {
    if (!window.confirm(`Delete credentials for ${course}?`)) return;

    try {
      await post("/delete-credentials", { email: userEmail, course });
      setCredentials((prev) => prev.filter((cred) => cred.course !== course));
      alert(" Credentials deleted");
    } catch (error) {
      console.error("Delete failed:", error);
      alert("Error deleting credentials.");
    }
  };

  const handleEditCredential = (cred) => {
    setEditCredential({
      ...cred,
      new_username: cred.username,
      new_password: "",
      new_cvv: cred.cvv || "",
    });
  };

  const handleUpdateCredential = async () => {
    if (!editCredential.new_username || !editCredential.new_password) {
      alert("Please fill in both fields.");
      return;
    }
    
    // CVV required for Mission Trails
    if (editCredential.course === "Mission Trails Golf Course") {
      if (!editCredential.new_cvv) {
        alert("CVV is required for Mission Trails.");
        return;
      }
      const cvvTrimmed = editCredential.new_cvv.toString().trim();
      if (!cvvTrimmed.match(/^\d{3,4}$/)) {
        alert("CVV must be 3-4 digits.");
        return;
      }
    }

    try {
      const payload = {
        email: userEmail,
        course: editCredential.course,
        new_username: editCredential.new_username,
        new_password: editCredential.new_password,
      };
      
      // Add CVV if Mission Trails
      if (editCredential.course === "Mission Trails Golf Course") {
        payload.new_cvv = editCredential.new_cvv.toString().trim();
      }
      
      await post("/update-credentials", payload);

      setCredentials((prev) =>
        prev.map((cred) =>
          cred.course === editCredential.course
            ? { ...cred, username: editCredential.new_username, cvv: editCredential.new_cvv }
            : cred
        )
      );
      alert(" Updated!");
      setEditCredential(null);
    } catch (error) {
      console.error("Update failed:", error);
      alert("Error updating credentials.");
    }
  };

  return (
    <div className="relative w-screen h-screen text-white" style={{ fontFamily: 'League Spartan, sans-serif' }}>
      {/* Background Image */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: "url('/images/tpn.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "scroll",
        }}
      />
      {/* Vintage Overlay */}
      <VintageFilter />

      {/* Content */}
      <div className="relative z-20 max-w-4xl mx-auto pt-32 px-6">
        <h1 className="text-4xl font-bold text-center mb-8 drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
          My Credentials
        </h1>

        <div className="flex justify-center mb-8">
          <button
            onClick={() => navigate("/add-credentials")}
            className="px-6 py-2 bg-blue-600 text-white rounded-full shadow-md hover:bg-blue-700 transition"
          >
            + Add Credentials
          </button>
        </div>

        {credentials.length > 0 ? (
          <ul className="flex flex-col gap-4">
            {credentials.map((cred) => (
              <li
                key={cred.course}
                className="bg-white/90 text-black rounded-lg shadow-lg p-4 flex justify-between items-center"
              >
                <div>
                  <p>
                    <span className="font-semibold">Course:</span> {cred.course}
                  </p>
                  <p>
                    <span className="font-semibold">Username:</span> {cred.username}
                  </p>
                  <p>
                    <span className="font-semibold">Password:</span> ••••••••
                  </p>
                  {cred.course === "Mission Trails Golf Course" && cred.cvv && (
                    <p>
                      <span className="font-semibold">CVV:</span> •••
                    </p>
                  )}
                </div>
                <div className="space-x-2">
                  <button
                    onClick={() => handleEditCredential(cred)}
                    className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteCredential(cred.course)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-white/80 text-center">No saved credentials.</p>
        )}

        {editCredential && (
          <div className="fixed inset-0 overflow-y-auto flex items-center justify-center bg-black bg-opacity-50 p-4 z-20">
            <div className="bg-white p-6 rounded-lg shadow-lg w-96 text-black">
              <h2 className="text-2xl font-semibold mb-4">
                Edit Credentials for {editCredential.course}
              </h2>
              <input
                type="text"
                placeholder="New Username"
                className="p-2 border rounded w-full mb-4"
                value={editCredential.new_username}
                onChange={(e) =>
                  setEditCredential({ ...editCredential, new_username: e.target.value })
                }
              />
              <input
                type="password"
                placeholder="New Password"
                className="p-2 border rounded w-full mb-4"
                value={editCredential.new_password}
                onChange={(e) =>
                  setEditCredential({ ...editCredential, new_password: e.target.value })
                }
              />
              {editCredential.course === "Mission Trails Golf Course" && (
                <>
                  <input
                    type="text"
                    placeholder="CVV (last 3-4 digits of card)"
                    maxLength="4"
                    className="p-2 border-2 border-orange-500 rounded w-full mb-4 bg-yellow-50"
                    value={editCredential.new_cvv || ""}
                    onChange={(e) =>
                      setEditCredential({ 
                        ...editCredential, 
                        new_cvv: e.target.value.replace(/\D/g, '') 
                      })
                    }
                  />
                </>
              )}
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setEditCredential(null)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateCredential}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SelectCourseForCredentials = () => {
  const navigate = useNavigate();
  const [selectedCourse, setSelectedCourse] = useState("");

  const courses = [
    "Balboa Park Golf Course",
    "Torrey Pines North",
    "Torrey Pines South",
    "Mission Bay Golf Course",
    "Mission Trails Golf Course",
    "The Vineyard at Escondido"
  ];

  const handleNext = () => {
    if (!selectedCourse) {
      alert("Please select a course.");
      return;
    }
    navigate("/add-credentials-form", { state: { selectedCourse } });
  };

  return (
    <div className="container mx-auto p-6 pt-12 sm:pt-24 md:pt-32 text-black">
      <h1 className="text-3xl font-bold text-center mb-6">Select a Course</h1>
      <div className="flex flex-col items-center">
        <select
          value={selectedCourse}
          onChange={(e) => setSelectedCourse(e.target.value)}
          className="border p-3 rounded-lg w-full max-w-md bg-white shadow-md"
        >
          <option value="">Select a Course</option>
          {courses.map((course) => (
            <option key={course} value={course}>{course}</option>
          ))}
        </select>

        <button
          onClick={handleNext}
          className="mt-4 px-5 py-3 bg-green-600 text-white rounded-lg shadow-md hover:bg-green-700 transition"
        >
          Next →
        </button>
      </div>
    </div>
  );
};

const AddCredentialsForm = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [selectedCourse, setSelectedCourse] = useState(
    location.state?.selectedCourse || ""
  );
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [cvv, setCvv] = useState("");

  // Removed unused FOREUP_PAYMENT_URL

  // Removed Non Resident logic

  const isMissionTrails = selectedCourse === "Mission Trails Golf Course";

  const handleSubmit = async () => {
    if (!selectedCourse) {
      alert("Please select a course.");
      return;
    }
    if (!username || !password) {
      alert("Please fill in both fields.");
      return;
    }
    
    // CVV required for Mission Trails
    if (isMissionTrails && !cvv) {
      alert("CVV is required for Mission Trails.");
      return;
    }
    
    // Validate CVV format for Mission Trails
    if (isMissionTrails) {
      const cvvTrimmed = cvv.trim();
      if (!cvvTrimmed.match(/^\d{3,4}$/)) {
        alert("CVV must be 3-4 digits.");
        return;
      }
    }

    try {
      const payload = {
        email: localStorage.getItem("userEmail"),
        course: selectedCourse,
        username,
        password,
      };
      
      // Add CVV if Mission Trails
      if (isMissionTrails) {
        payload.cvv = cvv.trim();
      }
      
      await post("/save-credentials", payload);

      alert("Credentials saved successfully!");
      navigate("/profile");
    } catch (error) {
      console.error("Error saving credentials:", error.response?.data || error.message);
      alert(
        "Error saving credentials: " +
          (error.response?.data?.error || error.message)
      );
    }
  };

  return (
    <div className="container mx-auto p-6 pt-12 sm:pt-24 md:pt-32">
      <h1 className="text-3xl font-bold text-center mb-6">
        Enter Credentials for {selectedCourse || "a Course"}
      </h1>

      <div className="flex flex-col items-center w-full">
        {/* Course Selector */}
        <select
          value={selectedCourse}
          onChange={(e) => setSelectedCourse(e.target.value)}
          className="border p-3 rounded-lg w-full max-w-md bg-white text-black shadow-md mb-4"
        >
          <option value="">Select Course</option>
          <option value="Balboa Park Golf Course">Balboa Park Golf Course</option>
          <option value="Torrey Pines North">Torrey Pines North</option>
          <option value="Torrey Pines South">Torrey Pines South</option>
          <option value="Mission Bay Golf Course">Mission Bay Golf Course</option>
          <option value="Mission Trails Golf Course">Mission Trails Golf Course</option>
          <option value="The Vineyard at Escondido">The Vineyard at Escondido</option>
        </select>

        {/* Removed Non Resident banner notice */}

        <input
          type="text"
          placeholder="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="border p-3 rounded-lg w-full max-w-md bg-white text-black shadow-md mb-3"
        />

        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="border p-3 rounded-lg w-full max-w-md bg-white text-black shadow-md"
        />

        {/* CVV field - only for Mission Trails */}
        {isMissionTrails && (
          <>
            <input
              type="text"
              placeholder="CVV (last 3-4 digits of card)"
              maxLength="4"
              value={cvv}
              onChange={(e) => setCvv(e.target.value.replace(/\D/g, ''))}
              className="border-2 border-orange-500 p-3 rounded-lg w-full max-w-md bg-yellow-50 text-black shadow-md mt-3"
            />
            
          </>
        )}

        <button
          onClick={handleSubmit}
          className="mt-4 px-5 py-3 bg-green-600 text-white rounded-lg shadow-md hover:bg-green-700 transition"
        >
          Save Credentials
        </button>
      </div>
    </div>
  );
};

const BookNow = ({ reservations }) => {
  const navigate = useNavigate();
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [earliestTime, setEarliestTime] = useState("");
  const [latestTime, setLatestTime] = useState("");
  const [players, setPlayers] = useState("1");
  const [activeStartDate, setActiveStartDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState("month");
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const [mobileCourseSelect, setMobileCourseSelect] = useState("");

  const MAX_DAYS_AHEAD = 90;
  const maxDate = new Date(Date.now() + MAX_DAYS_AHEAD * 24 * 60 * 60 * 1000);

  const courses = [
    "Balboa Park Golf Course",
    "Torrey Pines North",
    "Torrey Pines South",
    "Mission Bay Golf Course"
  ];

  const handleDateChange = (date) => {
    const dateString = date.toDateString();
    if (date.getTime() > maxDate.getTime()) {
      alert(`You can only select dates up to ${maxDate.toDateString()}.`);
      return;
    }
    setSelectedDates((prev) => {
      const next = prev.includes(dateString) ? prev.filter((d) => d !== dateString) : [...prev, dateString];
      if (next.length > 5) {
        alert("You can only create up to 5 active reservations.");
        return prev;
      }
      return next;
    });
  };

  const generateTimeOptions = (minHour = 6, minMinute = 0) => {
    const times = [];
    for (let h = minHour; h <= 19; h++) {
      for (let m = h === minHour ? minMinute : 0; m < 60; m += 15) {
        const hour = h > 12 ? h - 12 : h;
        const period = h < 12 ? "AM" : "PM";
        const minute = m.toString().padStart(2, "0");
        times.push(`${hour}:${minute} ${period}`);
      }
    }
    return times;
  };

  const getAvailableLatestTimes = () => {
    if (!earliestTime) return generateTimeOptions();
    const [hour, minute, period] = earliestTime.split(/[:\s]/);
    let minHour = parseInt(hour, 10);
    let minMinute = parseInt(minute, 10);
    if (period === "PM" && minHour !== 12) minHour += 12;
    if (period === "AM" && minHour === 12) minHour = 0;
    return generateTimeOptions(minHour, minMinute + 15);
  };

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  // Helper: returns a Set of "course|date" for all active reservations
  const getActiveCourseDatePairs = () => {
    return new Set(
      (reservations || [])
        .filter(r => (r.status || '').toLowerCase() !== 'canceled')
        .map(r => `${r.course}|${r.date}`)
    );
  };

  // Helper: returns a Set of "course|date" for current selection
  const getSelectedCourseDatePairs = () => {
    const pairs = new Set();
    selectedCourses.forEach(course => {
      selectedDates.forEach(date => {
        pairs.add(`${course}|${date}`);
      });
    });
    return pairs;
  };

  // Helper: checks if adding a course/date would be a duplicate
  const isDuplicateCourseDate = (course, date) => {
    const activePairs = getActiveCourseDatePairs();
    return activePairs.has(`${course}|${date}`);
  };

  // Helper: checks if any selected course/date is a duplicate
  const hasAnyDuplicateCourseDate = () => {
    const activePairs = getActiveCourseDatePairs();
    for (const pair of getSelectedCourseDatePairs()) {
      if (activePairs.has(pair)) return true;
    }
    return false;
  };

  // --- PATCH: Prevent duplicate course-date combos on submit ---
  const handleBookNow = async () => {
    if (hasAnyDuplicateCourseDate()) {
      alert("You already have an auto-booking for one or more of these course/date combinations. Remove duplicates before submitting.");
      return;
    }
    const email = localStorage.getItem("userEmail");
    if (!email) {
      alert("Please log in to book a reservation.");
      navigate("/auth");
      return;
    }
    if (!selectedDates.length || !earliestTime || !latestTime || !selectedCourses.length) {
      alert("Please select all required details before booking.");
      return;
    }
    try {
      for (const course of selectedCourses) {
        for (const date of selectedDates) {
          await post("/book", { email, date, earliestTime, latestTime, course, players });
          await sleep(200);
          await post("/start-watch", { email, date, course, players });
          await sleep(150);
        }
      }
      alert(" Reservation(s) saved and watchers started!");
      navigate("/profile");
    } catch (error) {
      alert("Booking failed: " + (error.response?.data?.error || error.message));
    }
  };

  return (
    <div className="relative min-h-screen w-screen flex items-start justify-center pt-12 pb-6 px-6">
      <div
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: "url('/images/tpn.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      />
      <VintageFilter />

      {/* Main Scorecard Container */}
      <div className="scorecard-container relative z-20 bg-[#e8dfc5] text-black shadow-2xl border-4 border-black">
        <>
          <div className="scorecard-content">
            <div className="grid grid-cols-1 md:grid-cols-2 min-h-[480px]">
          
          {/* LEFT SIDE: Course & Info */}
          <div className="flex flex-col justify-between">
            <div className="flex-grow flex flex-col">
              {/* Edge-to-Edge Header: Select Course(s) */}
              <h3 className="scorecard-label bg-[#e8dfc5] text-black text-center py-2 w-full">Select Course(s)</h3>
              

              <div className="border-b-2 border-black md:border-b-4 md:border-black w-full"></div>
              <div className="flex flex-col border-b-2 border-black md:border-b-4 md:border-black">
                {/* Show each selected course as a line with remove button */}
                {selectedCourses.map((course, idx) => (
                  <div key={course} className="flex items-center justify-between border-b-2 border-black h-12 px-6 bg-white/5">
                    <span className="scorecard-course-name">{course}</span>
                    <button
                      onClick={() => setSelectedCourses(selectedCourses.filter((_, i) => i !== idx))}
                      className="course-remove-btn"
                      aria-label={`Remove ${course}`}
                      type="button"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                {/* Show select line only if less than 10 courses selected */}
                {selectedCourses.length < 10 && (
                  <div className="flex items-center justify-between border-b-2 border-black h-12 px-6 bg-white/5">
                    <div className="flex-1">
                      {isMobile ? (
                        <select
                          className="w-full p-2 border border-black rounded"
                          value={mobileCourseSelect}
                          onChange={e => {
                            const val = e.target.value;
                            if (val && !selectedCourses.includes(val) && selectedCourses.length < 10) {
                              setSelectedCourses([...selectedCourses, val]);
                              setMobileCourseSelect("");
                            } else {
                              setMobileCourseSelect(val);
                            }
                          }}
                        >
                          <option value="">Select Course...</option>
                          {courses.filter((c) => !selectedCourses.includes(c)).map((c) => (
                            <option key={c} value={c} disabled={selectedDates.some(date => isDuplicateCourseDate(c, date))}>{c}</option>
                          ))}
                        </select>
                      ) : (
                        <Select
                          placeholder="Select Course..."
                          options={courses
                            .filter((c) => !selectedCourses.includes(c))
                            .map((c) => ({
                              label: c,
                              value: c,
                              isDisabled: selectedDates.some(date => isDuplicateCourseDate(c, date)),
                            }))}
                          value={null}
                          onChange={(opt) => {
                            if (opt && opt.value && selectedCourses.length < 10) setSelectedCourses([...selectedCourses, opt.value]);
                          }}
                          styles={{
                            control: (base) => ({
                              ...base,
                              fontFamily: 'Calibri, sans-serif',
                              borderRadius: "0px",
                              borderColor: "black",
                              borderWidth: "1px",
                              backgroundColor: "transparent",
                              boxShadow: "none",
                              minWidth: 180,
                              maxWidth: 320,
                            }),
                            menu: (base) => ({
                              ...base,
                              backgroundColor: "#e8dfc5",
                              borderRadius: "0px",
                              border: "2px solid black",
                            }),
                          }}
                          isClearable={false}
                        />
                      )}
                    </div>
                    <div style={{ width: 32 }} />
                  </div>
                )}
              </div>

              {/* (Removed duplicate grid block; only the new per-line select/courses grid is rendered above) */}
            </div>

            {/* Bottom Left Grid: Times & Players */}
            <div className="grid grid-cols-2 border-b-2 border-black md:border-b-4 md:border-black border-t-2 border-black md:border-t-4 md:border-black">
              {/* Times Column */}
              <div className="flex flex-col border-r-2 border-black md:border-r-4 md:border-black">
                <h3 className="scorecard-label text-center w-full">Times</h3>
                <div className="border-b-2 border-black md:border-b-4 md:border-black w-full"></div>
                <div className="p-6 flex flex-col gap-4 border-b-2 border-black md:border-b-4 md:border-black">
                  <div className="flex justify-center items-center border-b-2 border-black md:border-b-4 md:border-black h-12">
                    {isMobile ? (
                      <select
                        className="w-full p-2 border border-black rounded"
                        value={earliestTime}
                        onChange={e => {
                          setEarliestTime(e.target.value);
                          setLatestTime("");
                        }}
                      >
                        <option value="">Earliest</option>
                        {generateTimeOptions().map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    ) : (
                      <Select
                        classNamePrefix="time-select"
                        placeholder="Earliest"
                        options={generateTimeOptions().map(t => ({ label: t, value: t }))}
                        value={earliestTime ? { label: earliestTime, value: earliestTime } : null}
                        onChange={opt => {
                          setEarliestTime(opt ? opt.value : "");
                          setLatestTime("");
                        }}
                        styles={{
                          control: base => ({
                            ...base,
                            minWidth: 140,
                            maxWidth: 180,
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            color: '#2d5a27',
                            background: 'transparent',
                            border: 'none',
                            boxShadow: 'none',
                            textAlign: 'center',
                          }),
                          singleValue: base => ({
                            ...base,
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            color: '#2d5a27',
                            textAlign: 'center',
                          }),
                          option: base => ({
                            ...base,
                            fontSize: '1rem',
                            textAlign: 'center',
                          }),
                          placeholder: base => ({
                            ...base,
                            fontSize: '1rem',
                            color: '#aaa',
                            textAlign: 'center',
                          })
                        }}
                      />
                    )}
                  </div>
                  <div className="flex justify-center items-center border-b-2 border-black md:border-b-4 md:border-black h-12">
                    {isMobile ? (
                      <select
                        className="w-full p-2 border border-black rounded"
                        value={latestTime}
                        onChange={e => setLatestTime(e.target.value)}
                        disabled={!earliestTime}
                      >
                        <option value="">Latest</option>
                        {getAvailableLatestTimes().map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    ) : (
                      <Select
                        classNamePrefix="time-select"
                        placeholder="Latest"
                        options={getAvailableLatestTimes().map(t => ({ label: t, value: t }))}
                        value={latestTime ? { label: latestTime, value: latestTime } : null}
                        onChange={opt => setLatestTime(opt ? opt.value : "")}
                        styles={{
                          control: base => ({
                            ...base,
                            minWidth: 140,
                            maxWidth: 180,
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            color: '#2d5a27',
                            background: 'transparent',
                            border: 'none',
                            boxShadow: 'none',
                            textAlign: 'center',
                          }),
                          singleValue: base => ({
                            ...base,
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            color: '#2d5a27',
                            textAlign: 'center',
                          }),
                          option: base => ({
                            ...base,
                            fontSize: '1rem',
                            textAlign: 'center',
                          }),
                          placeholder: base => ({
                            ...base,
                            fontSize: '1rem',
                            color: '#aaa',
                            textAlign: 'center',
                          })
                        }}
                        isDisabled={!earliestTime}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Players Column */}
              <div className="flex flex-col">
                <h3 className="scorecard-label text-center w-full">Players</h3>
                  <div className="border-b-2 border-black md:border-b-4 md:border-black w-full"></div>
                  <div className="px-4 flex justify-around items-center flex-grow h-12 border-b-2 border-black md:border-b-4 md:border-black">
                  {[1, 2, 3, 4].map((n) => (
                    <button
                      key={n}
                      onClick={() => setPlayers(String(n))}
                      className={`player-btn ${
                        players === String(n) 
                          ? "pencil-circle-green font-bold opacity-100" 
                          : "player-bubble-unselected opacity-40"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE: Calendar */}
          <div className="calendar-panel flex flex-col items-center text-black border-l-2 md:border-l-4 border-b-2 md:border-b-4 border-black h-full">
            {/* Edge-to-Edge Header with subtitle */}
            <div className="w-full flex justify-between items-center right-side-header-wrapper border-b-2 border-black md:border-b-4 md:border-black">
              <h3 className="scorecard-label text-center w-full">Select Dates</h3>
            </div>
            
            <div className="p-6 w-full">
              <div className="scorecard-calendar-wrapper w-full">
                <Calendar
                  key={selectedDates.join(",")}
                  view={calendarView}
                  onViewChange={(nextView) => {
                    if (nextView !== "month") {
                      setCalendarView("month");
                    }
                  }}
                  tileDisabled={({ date }) => {
                    // Disable if any selected course already has this date
                    return selectedCourses.some(course => isDuplicateCourseDate(course, date.toDateString()));
                  }}
                  onClickDay={handleDateChange}
                  tileClassName={({ date }) => {
                    const today = new Date();
                    today.setHours(0,0,0,0);
                    if (date < today) return "past-date";
                    if (selectedDates.includes(date.toDateString())) return "selected-date";
                    return null;
                  }}
                  minDate={new Date()}
                  maxDate={maxDate}
                  minDetail="month"
                  maxDetail="month"
                  showNeighboringMonth={true}
                  prev2Label={null}
                  next2Label={null}
                  activeStartDate={activeStartDate}
                  onActiveStartDateChange={({ activeStartDate }) => setActiveStartDate(activeStartDate)}
                  navigationLabel={({ date }) => (
                    <span
                      className="calendar-month-title"
                      style={{
                        color: '#000',
                        fontWeight: 900,
                        fontSize: '2.2rem',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        background: 'none',
                        boxShadow: 'none',
                        padding: 0,
                        margin: 0,
                        lineHeight: 1.1,
                        display: 'block',
                      }}
                    >
                      {date.toLocaleString('default', { month: 'long' })}
                    </span>
                  )}
                />
                <div className="w-full text-center mt-2 text-[15px] font-semibold text-black" style={{letterSpacing: '0.02em'}}>
                  *Max 5 dates / 90 days ahead
                </div>
              </div>
            </div>
          </div>
        </div>

        </div>

          {/* Action Footer */}
          <div className="border-b border-black p-6 bg-black/5 flex flex-col items-center">
            <button
              onClick={handleBookNow}
              className="bg-[#2d5a27] text-white px-16 py-4 rounded-sm font-bold uppercase tracking-widest shadow-[4px_4px_0px_#000] hover:bg-[#1e3d1a] active:translate-y-1 active:shadow-none transition-all flex items-center gap-2 text-lg"
            >
              Submit Auto-Booking <span className="text-xl">→</span>
            </button>
          </div>

        </>
      </div>
  </div>
  );
};

const TeeTimeNotify = ({ reservations }) => {
      const [mobileCourseSelect, setMobileCourseSelect] = useState("");
    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);
    useEffect(() => {
      const handleResize = () => setIsMobile(window.innerWidth <= 768);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);
  // --- BookNow scorecard UI and logic, but for notifications ---
  const navigate = useNavigate();
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [earliestTime, setEarliestTime] = useState("");
  const [latestTime, setLatestTime] = useState("");
  // Multi-select for players
  const [players, setPlayers] = useState(["1"]);
  const [activeStartDate, setActiveStartDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState("month");

  const MAX_DAYS_AHEAD = 90;
  const maxDate = new Date(Date.now() + MAX_DAYS_AHEAD * 24 * 60 * 60 * 1000);

  const courses = [
    "Balboa Park Golf Course",
    "Torrey Pines North",
    "Torrey Pines South",
    "Mission Bay Golf Course",
    "Mission Trails Golf Course",
    "Coronado Golf Course",
    "The Vineyard at Escondido"
  ];

  const handleDateChange = (date) => {
    const dateString = date.toDateString();
    if (date.getTime() > maxDate.getTime()) {
      alert(`You can only select dates up to ${maxDate.toDateString()}.`);
      return;
    }
    setSelectedDates((prev) => {
      const next = prev.includes(dateString) ? prev.filter((d) => d !== dateString) : [...prev, dateString];
      if (next.length > 5) {
        alert("You can only create up to 5 active notifications.");
        return prev;
      }
      return next;
    });
  };

  const generateTimeOptions = (minHour = 6, minMinute = 0) => {
    const times = [];
    for (let h = minHour; h <= 19; h++) {
      for (let m = h === minHour ? minMinute : 0; m < 60; m += 15) {
        const hour = h > 12 ? h - 12 : h;
        const period = h < 12 ? "AM" : "PM";
        const minute = m.toString().padStart(2, "0");
        times.push(`${hour}:${minute} ${period}`);
      }
    }
    return times;
  };

  const getAvailableLatestTimes = () => {
    if (!earliestTime) return generateTimeOptions();
    const [hour, minute, period] = earliestTime.split(/[:\s]/);
    let minHour = parseInt(hour, 10);
    let minMinute = parseInt(minute, 10);
    if (period === "PM" && minHour !== 12) minHour += 12;
    if (period === "AM" && minHour === 12) minHour = 0;
    return generateTimeOptions(minHour, minMinute + 15);
  };

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  // Helper: returns a Set of "course|date" for all active reservations
  const getActiveCourseDatePairs = () => {
    return new Set(
      (reservations || [])
        .filter(r => (r.status || '').toLowerCase() !== 'canceled')
        .map(r => `${r.course}|${r.date}`)
    );
  };

  // Helper: returns a Set of "course|date" for current selection
  const getSelectedCourseDatePairs = () => {
    const pairs = new Set();
    selectedCourses.forEach(course => {
      selectedDates.forEach(date => {
        pairs.add(`${course}|${date}`);
      });
    });
    return pairs;
  };

  // Helper: checks if adding a course/date would be a duplicate
  const isDuplicateCourseDate = (course, date) => {
    const activePairs = getActiveCourseDatePairs();
    return activePairs.has(`${course}|${date}`);
  };

  // Helper: checks if any selected course/date is a duplicate
  const hasAnyDuplicateCourseDate = () => {
    const activePairs = getActiveCourseDatePairs();
    for (const pair of getSelectedCourseDatePairs()) {
      if (activePairs.has(pair)) return true;
    }
    return false;
  };

  const handleTeeTimeNotify = async () => {
    if (hasAnyDuplicateCourseDate()) {
      alert("You already have a notification for one or more of these course/date combinations. Remove duplicates before submitting.");
      return;
    }
    const email = localStorage.getItem("userEmail");
    if (!email) {
      alert("Please log in to set a notification.");
      navigate("/auth");
      return;
    }
    if (!selectedDates.length || !earliestTime || !latestTime || !selectedCourses.length || !players.length) {
      alert("Please select all required details before setting a notification.");
      return;
    }
    try {
      for (const course of selectedCourses) {
        for (const date of selectedDates) {
          // Send all selected player counts in one reservation per course/date
          await post("/book", { email, date, earliestTime, latestTime, course, players });
          await sleep(200);
          await post("/start-notify", { email, date, course, players });
          await sleep(150);
        }
      }
      alert(" Notification(s) saved and notifiers started!");
      navigate("/profile");
    } catch (error) {
      alert("Notification failed: " + (error.response?.data?.error || error.message));
    }
  };

  return (
    <div className="relative min-h-screen w-screen flex items-start justify-center pt-12 pb-6 px-6">
      <div
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: "url('/images/tpn.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      />
      <VintageFilter />
      <div className="scorecard-container relative z-20 text-black shadow-2xl border-4 border-black" style={{ backgroundColor: '#fff', background: '#fff !important' }}>
        <>
          <div className="scorecard-content">
            <div className="grid grid-cols-1 md:grid-cols-2 min-h-[480px]">

          {/* LEFT SIDE: Course & Info */}
          <div className="flex flex-col justify-between ttn-left-col-bg">
            <div className="flex-grow flex flex-col">
              {/* Edge-to-Edge Header: Select Course(s) */}
              <h3 className="scorecard-label bg-[#1e3a8a] text-white text-center py-2 w-full">Select Course(s)</h3>


              <div className="border-b-2 border-black md:border-b-4 md:border-black w-full"></div>
              <div className="flex flex-col border-b-2 border-black md:border-b-4 md:border-black ttn-course-blank-bg">
                {/* Show each selected course as a line with remove button */}
                {selectedCourses.map((course, idx) => (
                  <div key={course} className="flex items-center justify-between border-b-2 border-black h-12 px-6 bg-white ttn-course-row-bg">
                    <span className="scorecard-course-name">{course}</span>
                    <button
                      onClick={() => setSelectedCourses(selectedCourses.filter((_, i) => i !== idx))}
                      className="course-remove-btn"
                      aria-label={`Remove ${course}`}
                      type="button"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                {/* Show select line only if less than 10 courses selected */}
                {selectedCourses.length < 10 && (
                  <div className="flex items-center justify-between border-b-2 border-black h-12 px-6 bg-white">
                    <div className="flex-1">
                      {isMobile ? (
                        <select
                          className="w-full p-2 border border-black rounded"
                          value={mobileCourseSelect}
                          onChange={e => {
                            const val = e.target.value;
                            if (val && !selectedCourses.includes(val) && selectedCourses.length < 10) {
                              setSelectedCourses([...selectedCourses, val]);
                              setMobileCourseSelect("");
                            } else {
                              setMobileCourseSelect(val);
                            }
                          }}
                        >
                          <option value="">Select Course...</option>
                          {courses.filter((c) => !selectedCourses.includes(c)).map((c) => (
                            <option key={c} value={c} disabled={selectedDates.some(date => isDuplicateCourseDate(c, date))}>{c}</option>
                          ))}
                        </select>
                      ) : (
                        <Select
                          placeholder="Select Course..."
                          options={courses
                            .filter((c) => !selectedCourses.includes(c))
                            .map((c) => ({
                              label: c,
                              value: c,
                              isDisabled: selectedDates.some(date => isDuplicateCourseDate(c, date)),
                            }))}
                          value={null}
                          onChange={(opt) => {
                            if (opt && opt.value && selectedCourses.length < 10) setSelectedCourses([...selectedCourses, opt.value]);
                          }}
                          styles={{
                            control: (base) => ({
                              ...base,
                              fontFamily: 'Calibri, sans-serif',
                              borderRadius: "0px",
                              borderColor: "black",
                              borderWidth: "1px",
                              backgroundColor: "transparent",
                              boxShadow: "none",
                              minWidth: 180,
                              maxWidth: 320,
                            }),
                            menu: (base) => ({
                              ...base,
                              backgroundColor: "#fff",
                              borderRadius: "0px",
                              border: "2px solid black",
                            }),
                          }}
                          isClearable={false}
                        />
                      )}
                    </div>
                    <div style={{ width: 32 }} />
                  </div>
                )}
              </div>

              {/* (Removed duplicate grid block; only the new per-line select/courses grid is rendered above) */}
            </div>

            {/* Bottom Left Grid: Times & Players */}
            <div className="grid grid-cols-2 border-b-2 border-black md:border-b-4 md:border-black border-t-2 border-black md:border-t-4 md:border-black">
              {/* Times Column */}
              <div className="flex flex-col border-r-2 border-black md:border-r-4 md:border-black">
                <h3 className="scorecard-label bg-[#1e3a8a] text-white text-center w-full">Times</h3>
                <div className="border-b-2 border-black md:border-b-4 md:border-black w-full"></div>
                <div className="p-6 flex flex-col gap-4 border-b-2 border-black md:border-b-4 md:border-black bg-white">
                  <div className="flex justify-center items-center border-b-2 border-black md:border-b-4 md:border-black h-12">
                    {isMobile ? (
                      <select
                        className="w-full p-2 border border-black rounded"
                        value={earliestTime}
                        onChange={e => {
                          setEarliestTime(e.target.value);
                          setLatestTime("");
                        }}
                      >
                        <option value="">Earliest</option>
                        {generateTimeOptions().map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    ) : (
                      <Select
                        classNamePrefix="time-select"
                        placeholder="Earliest"
                        options={generateTimeOptions().map(t => ({ label: t, value: t }))}
                        value={earliestTime ? { label: earliestTime, value: earliestTime } : null}
                        onChange={opt => {
                          setEarliestTime(opt ? opt.value : "");
                          setLatestTime("");
                        }}
                        styles={{
                          control: base => ({
                            ...base,
                            minWidth: 140,
                            maxWidth: 180,
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            color: '#2d5a27',
                            background: 'transparent',
                            border: 'none',
                            boxShadow: 'none',
                            textAlign: 'center',
                          }),
                          singleValue: base => ({
                            ...base,
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            color: '#2d5a27',
                            textAlign: 'center',
                          }),
                          option: base => ({
                            ...base,
                            fontSize: '1rem',
                            textAlign: 'center',
                          }),
                          placeholder: base => ({
                            ...base,
                            fontSize: '1rem',
                            color: '#aaa',
                            textAlign: 'center',
                          })
                        }}
                        isClearable={false}
                      />
                    )}
                  </div>
                  <div className="flex justify-center items-center border-b-2 border-black md:border-b-4 md:border-black h-12">
                    {isMobile ? (
                      <select
                        className="w-full p-2 border border-black rounded"
                        value={latestTime}
                        onChange={e => setLatestTime(e.target.value)}
                        disabled={!earliestTime}
                      >
                        <option value="">Latest</option>
                        {getAvailableLatestTimes().map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    ) : (
                      <Select
                        classNamePrefix="time-select"
                        placeholder="Latest"
                        options={getAvailableLatestTimes().map(t => ({ label: t, value: t }))}
                        value={latestTime ? { label: latestTime, value: latestTime } : null}
                        onChange={opt => setLatestTime(opt ? opt.value : "")}
                        styles={{
                          control: base => ({
                            ...base,
                            minWidth: 140,
                            maxWidth: 180,
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            color: '#2d5a27',
                            background: 'transparent',
                            border: 'none',
                            boxShadow: 'none',
                            textAlign: 'center',
                          }),
                          singleValue: base => ({
                            ...base,
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            color: '#2d5a27',
                            textAlign: 'center',
                          }),
                          option: base => ({
                            ...base,
                            fontSize: '1rem',
                            textAlign: 'center',
                          }),
                          placeholder: base => ({
                            ...base,
                            fontSize: '1rem',
                            color: '#aaa',
                            textAlign: 'center',
                          })
                        }}
                        isDisabled={!earliestTime}
                        isClearable={false}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Players Column */}
              <div className="flex flex-col">
                <h3 className="scorecard-label bg-[#1e3a8a] text-white text-center w-full">Players</h3>
                  <div className="border-b-2 border-black md:border-b-4 md:border-black w-full"></div>
                  <div className="px-4 flex justify-around items-center flex-grow h-12 border-b-2 border-black md:border-b-4 md:border-black bg-white">
                  {[1, 2, 3, 4].map((n) => (
                    <button
                      key={n}
                      onClick={() =>
                        setPlayers((prev) =>
                          prev.includes(String(n))
                            ? prev.filter((p) => p !== String(n))
                            : [...prev, String(n)]
                        )
                      }
                      className={`player-btn ${
                        players.includes(String(n))
                          ? "pencil-circle-green font-bold opacity-100"
                          : "player-bubble-unselected opacity-40"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE: Calendar */}
          <div className="calendar-panel flex flex-col items-center text-black border-l-2 md:border-l-4 border-b-2 md:border-b-4 border-black h-full bg-white">
            {/* Edge-to-Edge Header with subtitle */}
            <div className="w-full flex justify-between items-center right-side-header-wrapper border-b-2 border-black md:border-b-4 md:border-black bg-[#1e3a8a]">
              <h3 className="scorecard-label text-white text-center w-full bg-[#1e3a8a]">Select Dates</h3>
            </div>

            <div className="p-6 w-full">
              <div className="scorecard-calendar-wrapper w-full">
                <Calendar
                  key={selectedDates.join(",")}
                  view={calendarView}
                  onViewChange={(nextView) => {
                    if (nextView !== "month") {
                      setCalendarView("month");
                    }
                  }}
                  tileDisabled={({ date }) => {
                    return selectedCourses.some(course => isDuplicateCourseDate(course, date.toDateString()));
                  }}
                  onClickDay={handleDateChange}
                  tileClassName={({ date }) => {
                    const today = new Date();
                    today.setHours(0,0,0,0);
                    if (date < today) return "past-date";
                    if (selectedDates.includes(date.toDateString())) return "selected-date";
                    return null;
                  }}
                  minDate={new Date()}
                  maxDate={maxDate}
                  minDetail="month"
                  maxDetail="month"
                  showNeighboringMonth={true}
                  prev2Label={null}
                  next2Label={null}
                  activeStartDate={activeStartDate}
                  onActiveStartDateChange={({ activeStartDate }) => setActiveStartDate(activeStartDate)}
                  navigationLabel={({ date }) => (
                    <span
                      className="calendar-month-title"
                      style={{
                        color: '#000',
                        fontWeight: 900,
                        fontSize: '2.2rem',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        background: 'none',
                        boxShadow: 'none',
                        padding: 0,
                        margin: 0,
                        lineHeight: 1.1,
                        display: 'block',
                      }}
                    >
                      {date.toLocaleString('default', { month: 'long' })}
                    </span>
                  )}
                />
                <div className="w-full text-center mt-2 text-[15px] font-semibold text-black" style={{letterSpacing: '0.02em'}}>
                  *Max 5 dates / 90 days ahead
                </div>
              </div>
            </div>
          </div>
        </div>

        </div>

          {/* Action Footer */}
          <div className="border-b border-black p-6 bg-[#1e3a8a] flex flex-col items-center">
            <button
              onClick={handleTeeTimeNotify}
              className="bg-[#2d5a27] text-white px-16 py-4 rounded-sm font-bold uppercase tracking-widest shadow-[4px_4px_0px_#000] hover:bg-[#1e3d1a] active:translate-y-1 active:shadow-none transition-all flex items-center gap-2 text-lg"
            >
              Submit Notifications <span className="text-xl">→</span>
            </button>
          </div>

        </>
      </div>
    </div>
  );
};

const WhatsNew = () => (
  <div
    className="min-h-screen bg-cover bg-center px-6 pt-32 text-white"
    style={{
      backgroundImage: "url('/images/tpn.jpeg')",
      fontFamily: "League Spartan, sans-serif",
    }}
  >
    <div className="bg-black bg-opacity-50 p-6 rounded-lg max-w-3xl mx-auto shadow-lg">
      <h1 className="text-4xl font-bold mb-6 text-center">What’s New</h1>
      <ul className="space-y-6 text-lg leading-relaxed">
        <li> <strong>11-09-2025:</strong> Adding number of concurrent watchers to 10</li>        
        <li> <strong>11-09-2025:</strong> Email notifications for bad/incorrect credentials</li>        
        <li> <strong>11-06-2025:</strong> Automatic Cleanup of Past Reservations. Old reservations are now automatically removed once their date has passed.</li>
        <li> <strong>11-06-2025:</strong> Added “What’s New” page to keep users up to date.</li>
      </ul>
      <VintageFilter />      
    </div>
  </div>
);

const ContactPage = () => {
  const [contactEmail, setContactEmail] = useState("");
  const [contactSubject, setContactSubject] = useState("");
  const [contactMessage, setContactMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API_BASE_URL}/contact`, {
        email: contactEmail,
        subject: contactSubject,
        message: contactMessage,
      });
      alert(res.data.message || "Message sent!");
      setContactEmail("");
      setContactSubject("");
      setContactMessage("");
    } catch (err) {
      alert("Failed to send message.");
    }
  };

  return (
    <div className="min-h-screen bg-cover bg-center px-6 pt-32 text-white"
         style={{ backgroundImage: "url('/images/tpn.jpeg')", fontFamily: 'League Spartan, sans-serif' }}>
      <div className="bg-black bg-opacity-50 p-6 rounded-lg max-w-xl mx-auto shadow-lg">
        <h1 className="text-3xl font-bold mb-6 text-center">Contact Us</h1>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="email"
            placeholder="Your email"
            required
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            className="w-full p-3 border rounded text-black"
          />
          <input
            type="text"
            placeholder="Subject"
            required
            value={contactSubject}
            onChange={(e) => setContactSubject(e.target.value)}
            className="w-full p-3 border rounded text-black"
          />
          <textarea
            placeholder="Message"
            required
            rows="6"
            value={contactMessage}
            onChange={(e) => setContactMessage(e.target.value)}
            className="w-full p-3 border rounded text-black"
          />
          <button type="submit" className="w-full bg-blue-600 text-white py-3 rounded hover:bg-blue-700">
            Send
          </button>
        </form>
        <VintageFilter />
      </div>
    </div>
  );
};

const Navbar = ({ isLoggedIn, handleLogout }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <>
      <nav
        className="py-4 px-6 shadow-md fixed w-full top-0 z-50"
        style={{
          backgroundColor: '#fef9f6',
          color: '#0b2239',
          fontFamily: '"League Spartan", sans-serif',
          fontSize: '1.1rem',
        }}
      >
        <div className="max-w-7xl mx-auto flex items-center w-full justify-start md:justify-between relative">
          {/* Left group: Hamburger, then Logo */}
          <div className="flex items-center md:hidden flex-1 justify-start">
            <button
              className="block focus:outline-none mr-2"
              style={{
                background: 'none',
                border: 'none',
                boxShadow: 'none',
                margin: 0,
                padding: 0,
                width: '44px',
                height: '44px',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
            >
              <svg className="h-7 w-7 text-black" fill="none" stroke="currentColor" strokeWidth="2"
                viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round"
                  d={menuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
              </svg>
            </button>
            <Link to="/" className="flex items-center gap-2">
              <img src="/images/logo.png" alt="SoCalTeeTimes Logo" className="h-12 w-auto" />
            </Link>
          </div>

          {/* Desktop Links (centered) */}
          <div className="hidden md:flex gap-10 items-center font-bold ml-0 justify-center flex-1">
            <Link to="/" className="flex items-center gap-2">
              <img src="/images/logo.png" alt="SoCalTeeTimes Logo" className="h-12 w-auto" />
            </Link>
            <Link to="/auto-book" className="hover:underline">Auto-Book</Link>
            <Link to="/tee-time-notify" className="hover:underline">Tee Time Notify</Link>
            <Link to="/how-it-works" className="hover:underline">How It Works</Link>
            <Link to="/whats-new" className="hover:underline">What’s New</Link>
            <Link to="/contact" className="hover:underline">Contact Us</Link>
          </div>

          {/* Desktop Auth/Profile (right) */}
          <div className="hidden md:flex items-center font-bold ml-6">
            {isLoggedIn ? (
              <div className="relative group cursor-pointer">
                <span>My Profile ▾</span>
                <div className="absolute top-full right-0 hidden group-hover:flex flex-col bg-white text-[#0b2239] shadow-md rounded z-50 min-w-[240px] text-lg font-normal">
                  <Link to="/profile" className="px-5 py-3 hover:bg-gray-100">Active Reservations</Link>
                  <Link to="/previous-reservations" className="px-5 py-3 hover:bg-gray-100">Previous Reservations</Link>
                  <Link to="/credentials" className="px-5 py-3 hover:bg-gray-100">My Credentials</Link>
                  <button
                    onClick={handleLogout}
                    className="px-5 py-3 text-left hover:bg-red-100 text-red-600"
                  >
                    Log Out
                  </button>
                </div>
              </div>
            ) : (
              <Link to="/auth" className="hover:underline">Sign In <span className='mx-1'>|</span> Join</Link>
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="md:hidden absolute left-0 top-full w-64 flex flex-col items-start gap-2 font-bold bg-[#fef9f6] px-4 py-6 shadow-2xl z-50 border-r border-gray-300">
            <Link to="/auto-book" onClick={() => setMenuOpen(false)} className="w-full py-2 pl-2 text-left hover:bg-gray-100 rounded">Auto-Book</Link>
            <Link to="/tee-time-notify" onClick={() => setMenuOpen(false)} className="w-full py-2 pl-2 text-left hover:bg-gray-100 rounded">Tee Time Notify</Link>
            <Link to="/how-it-works" onClick={() => setMenuOpen(false)} className="w-full py-2 pl-2 text-left hover:bg-gray-100 rounded">How It Works</Link>
            <Link to="/whats-new" onClick={() => setMenuOpen(false)} className="w-full py-2 pl-2 text-left hover:bg-gray-100 rounded">What’s New</Link>
            <Link to="/contact" onClick={() => setMenuOpen(false)} className="w-full py-2 pl-2 text-left hover:bg-gray-100 rounded">Contact Us</Link>

            {/* Divider */}
            <div className="w-full border-t border-gray-300 my-2" />

            {isLoggedIn ? (
              <>
                <Link to="/profile" onClick={() => setMenuOpen(false)} className="w-full py-2 pl-2 text-left hover:bg-gray-100 rounded">Active Reservations</Link>
                <Link to="/previous-reservations" onClick={() => setMenuOpen(false)} className="w-full py-2 pl-2 text-left hover:bg-gray-100 rounded">Previous Reservations</Link>
                <Link to="/credentials" onClick={() => setMenuOpen(false)} className="w-full py-2 pl-2 text-left hover:bg-gray-100 rounded">My Credentials</Link>
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    handleLogout();
                  }}
                  className="w-full py-2 pl-2 text-left text-red-600 hover:bg-red-50 rounded"
                >
                  Log Out
                </button>
              </>
            ) : (
              <Link to="/auth" onClick={() => setMenuOpen(false)} className="w-full py-2 pl-2 text-left hover:bg-gray-100 rounded">Sign In <span className='mx-1'>|</span> Join</Link>
            )}
          </div>
        )}
      </nav>
    </>
  );
};

const ConfirmEmail = () => {
  const [message, setMessage] = useState("Confirming...");

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("token");
    if (!token) {
      setMessage(" Invalid confirmation link.");
      return;
    }

    axios.post(`${API_BASE_URL}/confirm-email`, { token })
      .then(res => setMessage(res.data.message || " Email confirmed!"))
      .catch(() => setMessage(" Confirmation failed."));
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center text-white">
      <div className="bg-black bg-opacity-60 p-8 rounded-lg text-center max-w-md shadow-lg">
        <h1 className="text-2xl font-bold mb-4">Email Confirmation</h1>
        <p>{message}</p>
      </div>
    </div>
  );
};

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(localStorage.getItem("isLoggedIn") === "true");
  const [reservations, setReservations] = useState([]);

  const handleLogout = () => {
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("userEmail");
    setIsLoggedIn(false);
  };

  return (
    <Router>
      <div
        className="min-h-screen bg-cover bg-center text-white"
        style={{
          backgroundImage: "url('/images/tpn.jpeg')",
        }}
      >
        <Navbar isLoggedIn={isLoggedIn} handleLogout={handleLogout} />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/auto-book" element={<BookNow reservations={reservations} />} />
          <Route path="/tee-time-notify" element={<TeeTimeNotify reservations={reservations} />} />
          <Route path="/whats-new" element={<WhatsNew />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/auth" element={<AuthPage setIsLoggedIn={setIsLoggedIn} />} />
          <Route path="/profile" element={<ProfilePage setIsLoggedIn={setIsLoggedIn} setReservations={setReservations} />} />
          <Route path="/add-credentials" element={<SelectCourseForCredentials />} />
          <Route path="/add-credentials-form" element={<AddCredentialsForm />} />
          <Route path="/previous-reservations" element={<PreviousReservations />} />
          <Route path="/credentials" element={<CredentialsPage />} />
          <Route path="/confirm-email" element={<ConfirmEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

        </Routes>
      </div>
    </Router>
  );
}

export default App;
